import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Card, CardActions, CardContent, Stack, CircularProgress } from '@mui/material';
import { Form } from 'components/_forms';
import { AppContext } from 'views/App/context';
import { translate as t } from 'hooks/useTranslations';
import { useQueryClient } from 'react-query';
import { enqueueSnackbar } from 'notistack';
import DTBPackageFormFields from '../_components/DTBPackageFormFields';
import useManageDTBPackages from '../_data_source/useManageDTBPackage';
import useDTBPackage from '../_data_source/useDTBPackage';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import { cloneDTB } from '../../Library/_data_sources/manageDTB';
import errorHelper from 'helpers/errorHelper';
import type { ServerErrorResponse } from 'data_sources/constants';

const DTBPackageClone: React.FC = () => {
  const navigate = useNavigate();
  const { id: sourcePackageId } = useParams<{ id: string }>();
  const { currentOrganizationId: orgId, currentProfile } = useContext(AppContext);
  const queryClient = useQueryClient();
  const { create, addDtbs } = useManageDTBPackages();
  const { data: sourcePackage, isFetching } = useDTBPackage(orgId, sourcePackageId || '');

  const [packageName, setPackageName] = useState('');
  const [jurisdictionId, setJurisdictionId] = useState('');
  const [manualId, setManualId] = useState('');
  const [verticalId, setVerticalId] = useState('');
  const [level, setLevel] = useState<'AGENCY' | 'JURISDICTION' | 'GLOBAL'>('GLOBAL');
  const [isCloning, setIsCloning] = useState(false);
  const [isAddingDtbs, setIsAddingDtbs] = useState(false);

  // Pre-fill form when sourcePackage loads
  useEffect(() => {
    if (sourcePackage) {
      setPackageName(`${sourcePackage.name} Clone`);
      setJurisdictionId(sourcePackage.jurisdiction?.id || '');
      setManualId(sourcePackage.manual?.id || '');
      setVerticalId(sourcePackage.vertical?.id || '');
      setLevel(sourcePackage.level);
    }
  }, [sourcePackage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sourcePackage || !sourcePackage.dtbs) return;

    setIsCloning(true);
    try {
      await cloneDTB(
        orgId,
        sourcePackage.id,
        packageName,
        jurisdictionId || undefined,
        manualId || undefined,
        level,
        verticalId || undefined,
        () => {
          enqueueSnackbar(t('dtbs.packages.clone.success'), {
            variant: 'success',
          });
          queryClient.invalidateQueries(['dtbPackages']);
          navigate('..');
        },
        (err: ServerErrorResponse) => {
          errorHelper.enqueueErrors(err);
        }
      );
    } catch (error) {
      enqueueSnackbar(t('dtbs.packages.clone.error'), { variant: 'error' });
      console.error('Error cloning DTB Package:', error);
    } finally {
      setIsCloning(false);
    }
  };

  if (isFetching || !sourcePackage || !sourcePackage.dtbs) {
    return (
      <TakeoverLayout
        pageTitle={t('dtbs.packages.clone.title')}
        backUrl='..'
      >
        <Card
          sx={{
            maxWidth: 800,
            mx: 'auto',
            width: '100%',
            p: 4,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Card>
      </TakeoverLayout>
    );
  }

  return (
    <TakeoverLayout
      pageTitle={t('dtbs.packages.clone.title')}
      backUrl='..'
    >
      <Card
        sx={{ maxWidth: 800, mx: 'auto', width: '100%' }}
        className='lex-form'
      >
        <Form onSubmit={handleSubmit}>
          <CardContent>
            <DTBPackageFormFields
              orgId={orgId || ''}
              packageName={packageName}
              jurisdictionId={jurisdictionId}
              manualId={manualId}
              verticalId={verticalId}
              level={level}
              isNew={false}
              onPackageNameChange={setPackageName}
              onJurisdictionChange={setJurisdictionId}
              onManualChange={setManualId}
              onVerticalChange={setVerticalId}
              onLevelChange={setLevel}
            />
          </CardContent>
          <CardActions>
            <Box />
            <Stack
              direction='row'
              spacing={2}
            >
              <Button
                color='neutral'
                href='..'
              >
                {t('common.cancel')}
              </Button>
              <Button
                disabled={
                  isCloning ||
                  isAddingDtbs ||
                  !packageName ||
                  isFetching ||
                  !sourcePackage?.dtbs ||
                  (level === 'JURISDICTION' && !jurisdictionId)
                }
                component='button'
                type='submit'
                variant='contained'
                endIcon={isCloning || isAddingDtbs ? <CircularProgress size={16} /> : undefined}
              >
                {t('common.save')}
              </Button>
            </Stack>
          </CardActions>
        </Form>
      </Card>
    </TakeoverLayout>
  );
};

export default DTBPackageClone;
